import React from 'react';
import { Box, Button, Card, CardActions, CardContent, CardMedia, Typography } from '@mui/material';
import { Videocam as VideocamIcon, VideocamOff as VideocamOffIcon } from '@mui/icons-material';
import { defineMessages, useIntl } from 'react-intl';
import { useQueryClient, useMutation, useQuery } from 'react-query';
import API, { Types, useAuth, useCsrfToken } from '@wheeebo/polaris-api';
import { useSnackbar } from '@wheeebo/polaris-snackbar';
import { useErrorHandler } from '../errorHandler';
import { CircleLoader } from '../loader';
const VIDEO_CAPTURE_STATUS_RETRY_COUNT = 30;
const VIDEO_CAPTURE_STATUS_RETRY_INTERVAL_MSEC = 5000; // 5 seconds (30回 = 150秒)
const msgs = defineMessages({
    errorNoVehicle: {
        id: 'pages.member.mission.FlightPlanCard.error.noVehicle',
        defaultMessage: 'Vehicle not found',
    },
    errorNoOperator: {
        id: 'pages.member.mission.FlightPlanCard.error.noOperator',
        defaultMessage: 'Operator not found',
    },
    successCommandSent: {
        id: 'pages.member.mission.FlightPlanCard.success.commandSent',
        defaultMessage: 'Command sent',
    },
    successVideoCaptureStarted: {
        id: 'pages.member.mission.FlightPlanCard.success.videoCaptureStarted',
        defaultMessage: 'Video capture started',
    },
    successVideoCaptureStopped: {
        id: 'pages.member.mission.FlightPlanCard.success.videoCaptureStopped',
        defaultMessage: 'Video capture stopped',
    },
});
function VideoCaptureForm({ vehicle }) {
    const intl = useIntl();
    const { account } = useAuth();
    const queryClient = useQueryClient();
    const { csrfToken } = useCsrfToken();
    const { showSuccess } = useSnackbar();
    const { showAndEmitJsErrorEvent } = useErrorHandler();
    const [loadCounter, setLoadCounter] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [webrtcUrl, setWebrtcUrl] = React.useState(vehicle.activeVideoStreamUrl);
    const [cameraCodec, setCameraCodec] = React.useState(vehicle.cameraCodec || 'h265');
    const [cameraResolution, setCameraResolution] = React.useState(vehicle.cameraResolution || '720p');
    const {} = useQuery(loading ? API.Vehicles.getKey(vehicle.id) : '', () => (loading ? API.Vehicles.get(vehicle.id) : undefined), {
        enabled: !webrtcUrl && loading && loadCounter < VIDEO_CAPTURE_STATUS_RETRY_COUNT,
        refetchInterval: VIDEO_CAPTURE_STATUS_RETRY_INTERVAL_MSEC,
        onSettled: () => setLoadCounter((e) => e + 1),
        onSuccess: (vehicle) => {
            if (vehicle.activeVideoStreamUrl) {
                setLoading(false);
                setWebrtcUrl(vehicle.activeVideoStreamUrl);
            }
        },
    });
    const { mutate: startVideoCapture } = useMutation(async () => {
        if (!account) {
            return Promise.reject(intl.formatMessage(msgs.errorNoOperator));
        }
        setLoading(true);
        // 1. Save camera codec / resolution to Polaris DB via PUT /api/vehicles/:id
        await API.Vehicles.update(csrfToken, vehicle.id, { cameraCodec, cameraResolution });
        // 2. Start video capture on vehicle-agent
        return API.Vehicles.command(csrfToken, vehicle.id, account.id, Types.VehicleCommandStartVideoCapture, {});
    }, {
        onSuccess: async () => {
            queryClient.invalidateQueries(API.FlightPlans.CacheKey);
            showSuccess(intl.formatMessage(msgs.successVideoCaptureStarted));
        },
        onError: (e) => {
            showAndEmitJsErrorEvent(e);
            setLoading(false);
        },
    });
    const { mutate: stopVideoCapture } = useMutation(() => {
        setLoading(false);
        setWebrtcUrl(undefined);
        if (!account) {
            return Promise.reject(intl.formatMessage(msgs.errorNoOperator));
        }
        return API.Vehicles.command(csrfToken, vehicle.id, account.id, Types.VehicleCommandStopVideoCapture, {});
    }, {
        onSuccess: async () => {
            queryClient.invalidateQueries(API.FlightPlans.CacheKey);
            showSuccess(intl.formatMessage(msgs.successVideoCaptureStopped));
        },
        onError: showAndEmitJsErrorEvent,
    });
    const handleClose = React.useCallback(() => {
        if (webrtcUrl) {
            stopVideoCapture();
        }
    }, [webrtcUrl, stopVideoCapture]);
    React.useEffect(() => {
        window.addEventListener('beforeunload', handleClose);
        return () => {
            window.removeEventListener('beforeunload', handleClose);
        };
    }, [handleClose]);
    return (React.createElement(Card, { sx: { p: 0, m: 0, borderWidth: 0 }, variant: "outlined" },
        React.createElement(CardContent, { sx: { p: 0, m: 0 } },
            React.createElement(Box, { sx: {
                    position: 'relative',
                    width: '100%',
                    minHeight: 240,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'common.black',
                    color: 'common.white',
                    overflow: 'hidden',
                } }, webrtcUrl ? (React.createElement(CardMedia, { component: "iframe", src: webrtcUrl, loading: "eager", allow: "autoplay; fullscreen; encrypted-media; picture-in-picture; display-capture; camera; microphone", allowFullScreen: true, "data-testid": "polaris-vehicle-stream-iframe", sx: { width: '100%', minHeight: 320, border: 0, bgcolor: 'common.black' } })) : loading ? (React.createElement(CircleLoader, null)) : (React.createElement(Box, { sx: { textAlign: 'center', px: 2 } },
                React.createElement(Typography, { variant: "subtitle2", sx: { opacity: 0.9, mb: 0.5 } }, "Live Camera Preview"),
                React.createElement(Typography, { variant: "body2", sx: { opacity: 0.8 } }, "Camera stream will appear here after starting capture."))))),
        React.createElement(CardActions, { sx: { display: 'flex', flexDirection: 'column', gap: 2, p: 2 } },
            !webrtcUrl && (React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1, width: '100%' } },
                React.createElement(Box, { sx: { display: 'flex', gap: 1, width: '100%' } },
                    React.createElement(Button, { fullWidth: true, variant: cameraCodec === 'h264' ? 'contained' : 'outlined', onClick: () => setCameraCodec('h264'), disabled: loading }, "H.264 (AVC)"),
                    React.createElement(Button, { fullWidth: true, variant: cameraCodec === 'h265' ? 'contained' : 'outlined', onClick: () => setCameraCodec('h265'), disabled: loading }, "H.265 (HEVC)")),
                React.createElement(Box, { sx: { display: 'flex', gap: 1, width: '100%' } },
                    React.createElement(Button, { fullWidth: true, size: "small", variant: cameraResolution === '720p' ? 'contained' : 'outlined', onClick: () => setCameraResolution('720p'), disabled: loading }, "720p"),
                    React.createElement(Button, { fullWidth: true, size: "small", variant: cameraResolution === '1080p' ? 'contained' : 'outlined', onClick: () => setCameraResolution('1080p'), disabled: loading }, "1080p")))),
            webrtcUrl ? (React.createElement(Button, { fullWidth: true, "data-testid": "polaris-vehicle-stream-stop", startIcon: React.createElement(VideocamOffIcon, null), onClick: () => stopVideoCapture(), disabled: loading }, "Stop video capture and recording")) : (React.createElement(Button, { fullWidth: true, "data-testid": "polaris-vehicle-stream-start", startIcon: React.createElement(VideocamIcon, null), onClick: () => startVideoCapture(), disabled: loading }, "Start video capture and recording")))));
}
export default React.memo(VideoCaptureForm);
